
"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref as dbRef, set as dbSet, push, get } from "firebase/database"
import { getAuth } from "firebase/auth"
import { useLocation, useNavigate } from "react-router-dom"
import "./SteelOrderForm.css"
import { db, set } from "../Firebase/firebase-config"

const SteelOrderForm = () => {
  const location = useLocation()
  const product = location.state?.product || {}
  const navigate = useNavigate()

  // Form state
  const [userId, setUserId] = useState(null)
  const [companyName, setCompanyName] = useState("")
  const [userGstin, setUserGstin] = useState("")
  const [userPan, setUserPan] = useState("")
  const [userPhone, setUserPhone] = useState("")
  const [userEmail, setUserEmail] = useState("")

  // Order details
  const [deliveryAddress, setDeliveryAddress] = useState("")
  const [deliveryCity, setDeliveryCity] = useState("")
  const [deliveryState, setDeliveryState] = useState("")
  const [deliveryPincode, setDeliveryPincode] = useState("")
  const [selectedSteelCompany, setSelectedSteelCompany] = useState("")
  const [selectedGrade, setSelectedGrade] = useState("")
  const [selectedDiameter, setSelectedDiameter] = useState("")
  const [selectedQuantity, setSelectedQuantity] = useState("")
  const [deliveryDate, setDeliveryDate] = useState("")
  const [contactPerson, setContactPerson] = useState("")
  const [contactPhone, setContactPhone] = useState("")
  const [paymentTerms, setPaymentTerms] = useState("advance")
  const [remarks, setRemarks] = useState("")

  // UI state
  const [addressError, setAddressError] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ po: 0, payment: 0 })

  // WhatsApp integration
  const [apiKey] = useState("")
  const [campaignName] = useState("Altrange")
  const [destination] = useState("+919592080108")
  const [userName] = useState("")
  const [source] = useState("")
  const [mediaUrl] = useState("")
  const [mediaFilename] = useState("")
  const [templateParams] = useState([])
  const [tags] = useState([])
  const [response, setResponse] = useState({})
  const gifUrl = "/Images/New notification.gif"
  const [selectedSteelType, setSelectedSteelType] = useState("");

  // Steel companies in India
  const steelCompanies = [
    "Tata Steel",
    "JSW Steel",
    "SAIL (Steel Authority of India Limited)",
    "Jindal Steel & Power Ltd",
    "RINL (Rashtriya Ispat Nigam Limited)",
    "JSPL (Jindal Steel and Power Limited)",
    "Essar Steel",
    "ArcelorMittal Nippon Steel India",
    "Bhushan Steel",
    "Uttam Galva Steels",
    "Other",
  ]

  // Steel grades as per Indian standards (IS)
  const steelGrades = {
    "TMT Bars": ["Fe 415", "Fe 415D", "Fe 500", "Fe 500D", "Fe 550", "Fe 550D", "Fe 600"],
    "Structural Steel": [
      "IS 2062 E250 (Fe 410W)",
      "IS 2062 E300 (Fe 440)",
      "IS 2062 E350 (Fe 490)",
      "IS 2062 E410 (Fe 540)",
    ],
    "Stainless Steel": ["SS 304", "SS 316", "SS 410", "SS 430"],
    "Mild Steel": ["IS 2062 Gr. A", "IS 2062 Gr. B", "IS 2062 Gr. C"],
  }

  // Common diameters for TMT bars in mm
  const diameters = ["6mm", "8mm", "10mm", "12mm", "16mm", "20mm", "25mm", "28mm", "32mm", "36mm", "40mm"]

  useEffect(() => {
    const fetchUserDetails = async () => {
      const auth = getAuth()
      const user = auth.currentUser

      if (user) {
        const database = getDatabase()
        const userRef = dbRef(database, `profileDashboard/${user.uid}`)

        try {
          const snapshot = await get(userRef)
          if (snapshot.exists()) {
            const userData = snapshot.val()
            setCompanyName(userData.company_name || "")
            setUserGstin(userData.gstin || "")
            setUserPan(userData.pan || "")
            setUserPhone(userData.phone || "")
            setUserEmail(userData.email || "")
            setContactPerson(userData.contact_person || "")
            setContactPhone(userData.phone || "")
          } else {
            console.error("No user data found in profileDashboard.")
          }
        } catch (error) {
          console.error("Error fetching user details:", error)
        }
      } else {
        setError("User is not authenticated")
      }
    }

    fetchUserDetails()
  }, [])

  useEffect(() => {
    const fetchUserId = async () => {
      const auth = getAuth()
      const user = auth.currentUser
      if (user) {
        setUserId(user.uid)
      } else {
        setError("User is not authenticated")
      }
    }
    fetchUserId()
  }, [])

  const handleQuantityChange = (e) => {
    setSelectedQuantity(e.target.value)
  }

  const handleAddressChange = (e) => {
    let inputText = e.target.value;
    let words = inputText.trim().split(/\s+/).filter(Boolean); // split into words

    if (words.length <= 200) {
        setDeliveryAddress(inputText);
        setAddressError("");
    } else {
        setAddressError("Delivery Address cannot exceed 200 words");
        // truncate to 200 words
        setDeliveryAddress(words.slice(0, 200).join(" "));
    }
};


  const handleFormSubmit = (event) => {
    event.preventDefault()
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async (event) => {
    event.preventDefault()
    setShowConfirmation(false)

    function getIndianTimestamp() {
      const now = new Date()
      const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
      const istTime = new Date(now.getTime() + istOffset)
      return istTime.toISOString().replace("T", " ").replace("Z", "")
    }

    if (!selectedSteelCompany) {
      setError("Please select a steel company before submitting the form.")
      return
    }

    if (!userId) {
      setError("User is not authenticated")
      return
    }

    setLoading(true)
    try {
      const orderData = {
        brand: selectedSteelCompany || product.name || "",
        steelGrade: selectedSteelType && selectedGrade ? `${selectedSteelType} - ${selectedGrade}` : "",
        steelDiameter: selectedDiameter,
        deliveryAddress: `${deliveryAddress}, ${deliveryCity}, ${deliveryState} - ${deliveryPincode}`,
        quantity: selectedQuantity || "",
        paymentTerms: paymentTerms,
        deliveryDate: deliveryDate,
        remarks: remarks,
        timestamp: getIndianTimestamp(),
        status: "Pending",
        companyName,
        category: "steel",
      }

      // Write order to user's node
      const database = getDatabase()
      const userOrderRef = dbRef(database, `steelOrder/${userId}`)
      const newOrderRef = push(userOrderRef)

      await dbSet(newOrderRef, orderData)
      const orderId = newOrderRef.key

      const sendNotification = async (userId, orderId, title, message) => {
        const notificationRef = dbRef(db, `supplier_notifications/Steel Orders/${userId}/${orderId}`)
        const newNotification = {
          brand: selectedSteelCompany || product.name || "",
          steelGrade: selectedGrade,
          title,
          message,
          gifUrl,
          timestamp: getIndianTimestamp(),
          seen: false,
        }

        try {
          await set(notificationRef, newNotification)
        } catch (error) {
          console.error("Error sending notification:", error)
        }
      }

      const notificationMessage = `A new steel order has been placed by ${companyName}. Order details: ${selectedGrade} ${selectedDiameter}, Qty: ${selectedQuantity} tonnes, Delivery: ${deliveryDate}.`
      await sendNotification(userId, orderId, "New Steel Order", notificationMessage)

      // Reset form
      setDeliveryAddress("")
      setDeliveryCity("")
      setDeliveryState("")
      setDeliveryPincode("")
      setSelectedGrade("")
      setSelectedDiameter("")
      setSelectedQuantity("")
      setDeliveryDate("")
      setRemarks("")
      setError("")
      setSuccess(true)
    } catch (error) {
      setError("Error submitting order: " + error.message)
      console.error("Error submitting order:", error)
    } finally {
      setLoading(false)
      setUploadProgress({ po: 0, payment: 0 })
    }
  }

  const handleCancel = () => {
    setShowConfirmation(false)
  }


  const handleQuoteRequestClick = () => {
    navigate("/dashboard/quote-request") // Navigate to the chat page
  }

  // Get steel grade options based on selected type
  const getGradeOptions = () => {
    if (!selectedSteelType) return []; // nothing selected yet
    return steelGrades[selectedSteelType].map((grade) => (
      <option key={grade} value={grade}>
        {grade}
      </option>
    ));
  };


  // Form validation
  const isFormComplete =
    deliveryAddress &&
    deliveryCity &&
    deliveryState &&
    deliveryPincode &&
    selectedSteelCompany &&
    selectedGrade &&
    (selectedGrade.includes("TMT") ? selectedDiameter : true) &&
    selectedQuantity &&
    deliveryDate &&
    paymentTerms 

  return (
    <div className="container-fluid steel-order-container">
      <h1 className="steel-order-title">Steel Order Form</h1>

      <div className="steel-order-detail">
        {/* Left Sidebar */}
        <div className="steel-order-sidebar">
          <img
            src={product.img || "/placeholder.svg"}
            alt={product.name || "Steel Product"}
            className="steel-order-sidebar-image"
          />
          <h2 className="steel-order-sidebar-name">{selectedSteelCompany || product.name || "Steel Product"}</h2>
          <button type="button" className="steel-order-quote-btn" onClick={handleQuoteRequestClick}>
            Request a Quote
          </button>

        </div>

        {/* Right Content */}
        <div className="steel-order-content">
          <form onSubmit={handleFormSubmit}>
            <div className="steel-order-form-section">
              <h3 className="steel-order-section-title">Steel Specifications</h3>

              <div className="steel-order-row">
                <div className="steel-order-group">
                  <label htmlFor="steelCompany" className="steel-order-label">
                    Steel Manufacturer:
                  </label>
                  <select
                    id="steelCompany"
                    value={selectedSteelCompany}
                    onChange={(e) => setSelectedSteelCompany(e.target.value)}
                    required
                    className="steel-order-input"
                  >
                    <option value="">Select Steel Company</option>
                    {steelCompanies.map((company) => (
                      <option key={company} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="steel-order-group">
                  <label htmlFor="steelType" className="steel-order-label">
                    Steel Type:
                  </label>
                  <select
                    id="steelType"
                    value={selectedSteelType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setSelectedSteelType(newType); // ✅ save selected type
                      setSelectedGrade("");          // reset grade
                      setSelectedDiameter("");       // reset diameter
                    }}
                    required
                    className="steel-order-input"
                  >
                    <option value="">Select Steel Type</option>
                    {Object.keys(steelGrades).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>

                </div>
              </div>

              <div className="steel-order-row">
                <div className="steel-order-group">
                  <label htmlFor="steelGrade" className="steel-order-label">
                    Steel Grade:
                  </label>
                  <select
                    id="steelGrade"
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    required
                    className="steel-order-input"
                  >
                    <option value="">Select Grade</option>
                    {getGradeOptions()}
                  </select>
                </div>

                {selectedGrade && selectedGrade.includes("Fe") && (
                  <div className="steel-order-group">
                    <label htmlFor="diameter" className="steel-order-label">
                      Diameter:
                    </label>
                    <select
                      id="diameter"
                      value={selectedDiameter}
                      onChange={(e) => setSelectedDiameter(e.target.value)}
                      required
                      className="steel-order-input"
                    >
                      <option value="">Select Diameter</option>
                      {diameters.map((dia) => (
                        <option key={dia} value={dia}>
                          {dia}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="steel-order-row">
                <div className="steel-order-group">
                  <label htmlFor="quantity" className="steel-order-label">
                    Quantity (Tonnes):
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    value={selectedQuantity}
                    onChange={handleQuantityChange}
                    onWheel={(e) => e.target.blur()}
                    required

                    className="steel-order-input"
                  />
                </div>

                <div className="steel-order-group">
                  <label htmlFor="deliveryDate" className="steel-order-label">
                    Expected Delivery Date:
                  </label>
                  <input
                    type="date"
                    id="deliveryDate"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    required
                    min={new Date().toISOString().split("T")[0]}
                    className="steel-order-input"
                  />
                </div>
              </div>
            </div>

            <div className="steel-order-form-section">
              <h3 className="steel-order-section-title">Delivery Information</h3>

              <div className="steel-order-group full-width">
                <label htmlFor="deliveryAddress" className="steel-order-label">
                  Delivery Address:
                </label>
                <input
                  type="text"
                  id="deliveryAddress"
                  value={deliveryAddress}
                  onChange={handleAddressChange}
                  required
                  placeholder="Street address, landmark"
                  className="steel-order-input"
                  minLength={10}
                  
                />
                {addressError && <p className="steel-order-error">{addressError}</p>}
    <p className="char-count">
        {deliveryAddress.trim().split(/\s+/).filter(Boolean).length} / 200 words
    </p>
              </div>

              <div className="steel-order-row mt-3">
                <div className="steel-order-group">
                  <label htmlFor="deliveryCity" className="steel-order-label">
                    City:
                  </label>
                  <input
                    type="text"
                    id="deliveryCity"
                    value={deliveryCity}
                    onChange={(e) => setDeliveryCity(e.target.value)}
                    required
                    className="steel-order-input"
                  />
                </div>

                <div className="steel-order-group">
                  <label htmlFor="deliveryState" className="steel-order-label">
                    State:
                  </label>
                  <select
                    id="deliveryState"
                    value={deliveryState}
                    onChange={(e) => setDeliveryState(e.target.value)}
                    required
                    className="steel-order-input"
                  >
                    <option value="">Select State</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Delhi">Delhi</option>
                  </select>
                </div>

                <div className="steel-order-group">
                  <label htmlFor="deliveryPincode" className="steel-order-label">
                    Pincode:
                  </label>
                  <input
                    type="text"
                    id="deliveryPincode"
                    value={deliveryPincode}
                    onChange={(e) => setDeliveryPincode(e.target.value)}
                    required
                    maxLength={6}
                    pattern="[0-9]{6}"
                    placeholder="6-digit pincode"
                    className="steel-order-input"
                  />
                </div>
              </div>
            </div>

            <div className="steel-order-form-section">
              <h3 className="steel-order-section-title"> Payment</h3>

              <div className="steel-order-row">
                <div className="steel-order-group">
                  <label htmlFor="paymentTerms" className="steel-order-label">
                    Payment Terms:
                  </label>
                  <select
                    id="paymentTerms"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    required
                    className="steel-order-input"
                  >
                    <option value="advance">100% Advance</option>
                    <option value="partial">50% Advance, 50% Before Delivery</option>
                    <option value="credit">Credit (30 days)</option>
                    <option value="credit45">Credit (45 days)</option>
                    <option value="lc">Letter of Credit</option>
                  </select>
                </div>
              </div>

              <div className="steel-order-group full-width">
                <label htmlFor="remarks" className="steel-order-label steel-order-remark-label">
                  Remarks / Special Instructions:
                </label>
                <textarea
                  id="remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Any additional details, special requirements, or delivery instructions"
                  rows={3}
                  className="steel-order-input"
                />
              </div>
            </div>

            <div className="steel-order-action-buttons">
              <button
                type="submit"
                className={`steel-order-action-btn steel-order-order-btn ${isFormComplete ? "active" : ""}`}
                disabled={!isFormComplete || loading}
              >
                {loading ? "Processing..." : "Place Order"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="steel-order-modal-overlay">
          <div className="steel-order-confirmation-modal">
            <h3>Confirm Steel Order</h3>
            <p>Please review your order details:</p>
            <div className="steel-order-summary">
              <p>
                <strong>Product:</strong> {selectedSteelCompany} {selectedGrade} {selectedDiameter}
              </p>
              <p>
                <strong>Quantity:</strong> {selectedQuantity} tonnes
              </p>
              <p>
                <strong>Delivery:</strong> {deliveryDate} to {deliveryAddress}, {deliveryCity}
              </p>
              <p>
                <strong>Payment Terms:</strong>{" "}
                {paymentTerms === "advance"
                  ? "100% Advance"
                  : paymentTerms === "partial"
                    ? "50% Advance, 50% Before Delivery"
                    : paymentTerms === "credit"
                      ? "Credit (30 days)"
                      : paymentTerms === "credit45"
                        ? "Credit (45 days)"
                        : "Letter of Credit"}
              </p>
            </div>
            <div className="steel-order-modal-buttons">
              <button onClick={handleConfirmSubmit} className="steel-order-confirm-btn">
                Yes, Place Order
              </button>
              <button onClick={handleCancel} className="steel-order-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {success && (
        <div className="steel-order-modal-overlay">
          <div className="steel-order-success-modal">
            <div className="steel-order-success-icon">✓</div>
            <h3>Steel Order Submitted Successfully!</h3>
            <p>
              Your steel order has been placed and is being processed. You will receive updates on your order status.
            </p>
            <button onClick={() => setSuccess(false)} className="steel-order-close-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="steel-order-error-notification">
          <p>{error}</p>
          <button onClick={() => setError("")} className="steel-order-close-error-btn">
            ×
          </button>
        </div>
      )}
    </div>
  )
}

export default SteelOrderForm